import React from 'react';
import { Alert, AlertTitle, styled } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';

interface ErrorAlertProps {
  severity?: 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  onClose?: () => void;
  showIcon?: boolean;
}

const StyledAlert = styled(Alert)({
  borderRadius: '12px',
  padding: '16px 20px',
  marginBottom: '20px',
  border: 'none',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  backdropFilter: 'blur(10px)',
  overflow: 'visible',
  maxHeight: 'none',
  '& .MuiAlert-icon': {
    fontSize: '24px',
  },
  '& .MuiAlert-message': {
    padding: '0',
    overflow: 'visible',
    maxHeight: 'none',
  },
  '& .MuiAlertTitle-root': {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '4px',
    overflow: 'visible',
  },
  '& .MuiAlert-description': {
    fontSize: '14px',
    lineHeight: 1.5,
    overflow: 'visible',
    wordWrap: 'break-word',
  },
});

const StyledErrorAlert = styled(StyledAlert)({
  background: 'linear-gradient(145deg, #fef2f2, #fee2e2)',
  color: '#dc2626',
  border: '1px solid rgba(220, 38, 38, 0.2)',
  '& .MuiAlert-icon': {
    color: '#dc2626',
  },
});

const StyledWarningAlert = styled(StyledAlert)({
  background: 'linear-gradient(145deg, #fffbeb, #fef3c7)',
  color: '#d97706',
  border: '1px solid rgba(217, 119, 6, 0.2)',
  '& .MuiAlert-icon': {
    color: '#d97706',
  },
});

const StyledInfoAlert = styled(StyledAlert)({
  background: 'linear-gradient(145deg, #eff6ff, #dbeafe)',
  color: '#2563eb',
  border: '1px solid rgba(37, 99, 235, 0.2)',
  '& .MuiAlert-icon': {
    color: '#2563eb',
  },
});

export const ErrorAlert: React.FC<ErrorAlertProps> = ({
  severity = 'error',
  title,
  message,
  onClose,
  showIcon = true,
}) => {
  const getIcon = () => {
    if (!showIcon) return null;

    switch (severity) {
      case 'error':
        return <ErrorOutlineIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return <ErrorOutlineIcon />;
    }
  };

  const StyledComponent = severity === 'error'
    ? StyledErrorAlert
    : severity === 'warning'
    ? StyledWarningAlert
    : StyledInfoAlert;

  return (
    <StyledComponent
      severity={severity}
      icon={getIcon()}
      onClose={onClose}
    >
      {title && <AlertTitle>{title}</AlertTitle>}
      {message}
    </StyledComponent>
  );
};